'use client'
import { Box, Flex, Skeleton, Stack, Text } from '@mantine/core'
import { useTranslations } from 'next-intl'
import React, { Suspense } from 'react'

import { GlobalMetricsContentMobile } from '@/app/[locale]/price/_components/GlobalMetrics/mobile'
import { SectionErrorBoundary } from '@/components/error/boundary'
import PriceChange from '@/components/PriceChange'
import { useCurrencyAmounts } from '@/hooks/business/useCurrencyAmounts'
import { useIsResponsiveMobileView } from '@/hooks/responsive'
import { useGlobalMetrics } from '@/services/coin/market'
import { formatUpToDecimals } from '@/utils/number'
import { formatMarketCap, formatPercentage } from '@/utils/number/format'
import { useIsOgApp } from '@/utils/og'

import MetricItem from './MetricItem'
import styles from './style.module.css'

function GlobalMetricsFallback() {
  return (
    <Flex className={styles.gridContainer} rowGap={16}>
      {[0, 1, 2, 3, 4, 5].map((item, index) => (
        <Flex data-index={index} key={index} justify={'flex-start'}>
          <Box w={index === 0 ? { base: 343, lg: 230 } : { base: 163, lg: 187 }}>
            <Skeleton h={20} w={80} mb={8} radius="xl" className={styles.skeleton} />
            <Skeleton h={20} w={120} radius="xl" mb={8} className={styles.skeleton} />
          </Box>
          {index < 5 && <Box w={1} bg="rgba(255, 255, 255, 0.08)" h="100%" mr={24} />}
        </Flex>
      ))}
    </Flex>
  )
}

function GlobalMetricsContent({
  fearGreedSlot,
  globalMetricTFunc,
  isOGPrice,
}: {
  fearGreedSlot?: React.ReactNode
  globalMetricTFunc: ReturnType<typeof useTranslations>
  isOGPrice?: boolean
}) {
  const { data } = useGlobalMetrics()
  const globalMetricsT = globalMetricTFunc
  const t = useTranslations()

  const merketCap = data?.market_cap ?? 0
  const volume24H = data?.volume_24h ?? 0

  const {
    amounts: [{ formattedValue: formattedMarketCap }, { formattedValue: formattedVolume24h }],
  } = useCurrencyAmounts({
    amounts: [{ value: merketCap }, { value: volume24H }],
    valueFormatter: formatMarketCap,
    currencyFormatOptions: {},
  })

  let boxWidth = { firstItem: 275, restItem: 160 }

  if (isOGPrice) {
    boxWidth = { firstItem: 230, restItem: 187 }
  }

  const metrics = [
    {
      label: globalMetricsT('marketCap'),
      value: formattedMarketCap,
      subValue: <PriceChange value={data?.market_cap_change_rate ?? 0} />,
      labelColor: 'var(--text-primary)',
      showInline: true,
      hideBorder: false,
    },
    { label: t('coinTable.headers.24hVolume'), value: formattedVolume24h },
    {
      label: globalMetricsT('dominance'),
      value: `${formatPercentage((data?.btc_dominance_rate ?? 0) * 100)}% BTC`,
      hideBorder: false,
    },
    {
      label: globalMetricsT('ethGas'),
      value: `${formatUpToDecimals(data?.eth_gas ?? 0, 2)} Gwei`,
    },
    { label: globalMetricsT('coins'), value: formatMarketCap(data?.coins ?? 0) },
    {
      label: globalMetricsT('fearGreed'),
      value: fearGreedSlot ?? null,
      hideBorder: true,
    },
  ]

  return (
    <Flex className={styles.gridContainer} rowGap={16}>
      {metrics.map((item, index) => (
        <Flex data-index={index} key={item.label} justify={'flex-start'}>
          <Box
            w={
              index === 0
                ? { base: 343, lg: boxWidth.firstItem }
                : { base: 163, lg: boxWidth.restItem }
            }
          >
            <MetricItem {...item} />
          </Box>
          {index < metrics.length - 1 && (
            <Box w={1} bg="rgba(255, 255, 255, 0.08)" h="100%" mr={24} />
          )}
        </Flex>
      ))}
    </Flex>
  )
}

function GlobalMetricsInner({
  fearGreedSlot,
  isOGPrice,
}: {
  fearGreedSlot?: React.ReactNode
  isOGPrice?: boolean
}) {
  const isMobile = useIsResponsiveMobileView()
  const globalMetricsT = useTranslations('globalMetrics')
  return (
    <Stack w="100%" gap={16}>
      <Text
        component={'h1'}
        variant={isMobile ? 'heading2' : 'heading1'}
        c="#fff"
        maw={isOGPrice ? 'unset' : 'calc(90% - 80px)'}
      >
        {globalMetricsT('title')}
      </Text>
      <Box mr={'auto'}>
        {isMobile ? (
          <GlobalMetricsContentMobile fearGreedSlot={fearGreedSlot} />
        ) : (
          <Suspense fallback={<GlobalMetricsFallback />}>
            <GlobalMetricsContent
              fearGreedSlot={fearGreedSlot}
              globalMetricTFunc={globalMetricsT}
              isOGPrice={isOGPrice}
            />
          </Suspense>
        )}
      </Box>
    </Stack>
  )
}

export default function GlobalMetrics(props: {
  fearGreedSlot?: React.ReactNode
  isOGPrice?: boolean
}) {
  const isOgApp = useIsOgApp()
  if (isOgApp) return null

  // wrap only when you actually render
  const Wrapped = SectionErrorBoundary.with(GlobalMetricsInner)
  return <Wrapped {...props} />
}
